import { router } from '@inertiajs/react'

// ---------------------------------------------------------------------------
// feedbackDiagnostics — passive context capture for the "Meld feil" widget.
//
// Two module-level ring buffers record what the page itself cannot tell a
// caseworker after the fact: the last few JS errors, and the navigation trail
// that led here. Nothing leaves the browser until the reporter presses send —
// collect() is only called from the widget's submit path.
//
// Surface-agnostic and dumb: no fetching, no privileged data, listeners only.
// ---------------------------------------------------------------------------

const MAX_ENTRIES = 10
const MAX_MESSAGE_LENGTH = 300

export type DiagnosticEntry = { at: string; message: string }

const errors: DiagnosticEntry[] = []
const breadcrumbs: DiagnosticEntry[] = []

let installed = false

function push(buffer: DiagnosticEntry[], message: string) {
  buffer.push({
    at: new Date().toISOString(),
    message: message.slice(0, MAX_MESSAGE_LENGTH),
  })
  if (buffer.length > MAX_ENTRIES) buffer.shift()
}

/**
 * Start listening. Idempotent — the widget calls this on every mount, the
 * listeners attach once. Errors thrown before the shell mounts are missed;
 * acceptable for a debug aid.
 */
export function installFeedbackDiagnostics() {
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    push(errors, event.message || String(event.error ?? 'unknown error'))
  })
  window.addEventListener('unhandledrejection', (event) => {
    push(errors, `Unhandled rejection: ${String(event.reason)}`)
  })
  router.on('navigate', (event) => {
    push(breadcrumbs, event.detail.page.url)
  })
}

/** Snapshot for one report. Buffers keep running afterwards. */
export function collectFeedbackDiagnostics() {
  return {
    viewport: { width: window.innerWidth, height: window.innerHeight },
    errors: [...errors],
    breadcrumbs: [...breadcrumbs],
  }
}
